import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  ChevronLeft, Tag, Plus, Loader2, Trash2, Edit3, Ticket, Percent, Sparkles,
  Play, Pause, XCircle, CheckCircle2, Clock, AlertTriangle, Lock, Copy,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const TYPES = [
  { value: "percent_off", label: "% off", hint: "Percent off the vendor subtotal", Icon: Percent },
  { value: "flat_off",    label: "$ off", hint: "Flat cents off the vendor subtotal", Icon: Tag },
  { value: "bogo",        label: "BOGO",  hint: "Buy N, get 1 free (marketing badge)", Icon: Sparkles },
  { value: "free_shipping", label: "Free shipping", hint: "Waives delivery fee (marketing badge)", Icon: Ticket },
];

const STATUS_META = {
  active:    { label: "Active", tone: "emerald", Icon: CheckCircle2 },
  scheduled: { label: "Scheduled", tone: "cyan", Icon: Clock },
  paused:    { label: "Paused", tone: "amber", Icon: Pause },
  expired:   { label: "Expired", tone: "zinc", Icon: Clock },
  exhausted: { label: "Fully redeemed", tone: "zinc", Icon: AlertTriangle },
  archived:  { label: "Archived", tone: "zinc", Icon: XCircle },
};

function StatusPill({ status }) {
  const M = STATUS_META[status] || STATUS_META.paused;
  const Icon = M.Icon;
  const cls = {
    emerald: "bg-emerald-500/15 border-emerald-400/40 text-emerald-100",
    cyan: "bg-cyan-500/15 border-cyan-400/40 text-cyan-100",
    amber: "bg-amber-500/15 border-amber-400/40 text-amber-100",
    zinc: "bg-zinc-500/15 border-zinc-400/40 text-zinc-100",
  }[M.tone];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest border ${cls}`} data-testid={`promo-status-${status}`}>
      <Icon className="w-3 h-3" /> {M.label}
    </span>
  );
}

const blank = () => ({
  name: "",
  type: "percent_off",
  value: 10,
  code: "",
  code_public: false,
  auto_apply: false,
  min_subtotal_cents: 0,
  starts_at: "",
  expires_at: "",
  per_user_limit: 1,
  total_limit: 0,
  description: "",
  is_active: true,
});

export default function VendorPromotions() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const shopId = useMemo(() => (user?.user_id ? `shop_${user.user_id}` : null), [user]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!shopId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/shops/${shopId}/promotions`);
      setItems(data.items || []);
    } catch (e) {
      if (e.response?.status !== 403) toast.error(e.response?.data?.detail || "Couldn't load promotions");
    } finally { setLoading(false); }
  }, [shopId]);

  useEffect(() => { load(); }, [load]);

  if (user && !user.is_vendor) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center space-y-3" data-testid="promo-not-vendor">
        <Lock className="w-10 h-10 text-zinc-500 mx-auto" />
        <h1 className="text-3xl font-black tracking-tighter">Vendors only</h1>
        <Button onClick={() => navigate("/sell")} className="rounded-full bg-pink-500 hover:bg-pink-400 text-white h-11 px-5">Open Sell</Button>
      </div>
    );
  }

  const startCreate = () => { setForm(blank()); setEditing(null); setShowForm(true); };
  const startEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      type: p.type,
      value: p.value,
      code: p.code || "",
      code_public: !!p.code_public,
      auto_apply: !!p.auto_apply,
      min_subtotal_cents: p.min_subtotal_cents || 0,
      starts_at: p.starts_at ? p.starts_at.slice(0, 16) : "",
      expires_at: p.expires_at ? p.expires_at.slice(0, 16) : "",
      per_user_limit: p.per_user_limit || 1,
      total_limit: p.total_limit || 0,
      description: p.description || "",
      is_active: !!p.is_active,
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Give this promo a name.");
    if (form.type === "percent_off" && (Number(form.value) < 1 || Number(form.value) > 90)) return toast.error("Percent off must be 1–90.");
    if (form.type === "flat_off" && Number(form.value) < 1) return toast.error("Flat off in cents must be > 0.");
    if (form.type === "bogo" && Number(form.value) < 1) return toast.error("BOGO N must be at least 1.");
    if (form.auto_apply && form.code.trim()) return toast.error("A promo is either auto-applied OR requires a code — not both.");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        type: form.type,
        value: Number(form.value),
        code: form.code.trim() || null,
        code_public: !!form.code_public,
        auto_apply: !!form.auto_apply,
        min_subtotal_cents: Math.max(0, Number(form.min_subtotal_cents) || 0),
        starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        per_user_limit: Math.max(1, Number(form.per_user_limit) || 1),
        total_limit: Math.max(0, Number(form.total_limit) || 0),
        description: form.description || null,
        is_active: !!form.is_active,
      };
      if (editing) {
        await api.put(`/shops/${shopId}/promotions/${editing.promotion_id}`, payload);
        toast.success("Promotion updated.");
      } else {
        await api.post(`/shops/${shopId}/promotions`, payload);
        toast.success("Promotion created.");
      }
      setShowForm(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Archive "${p.name}"? Existing redemptions stay intact.`)) return;
    try {
      await api.delete(`/shops/${shopId}/promotions/${p.promotion_id}`);
      toast.success("Archived.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't archive");
    }
  };

  const togglePause = async (p) => {
    try {
      await api.put(`/shops/${shopId}/promotions/${p.promotion_id}`, { is_active: !p.is_active });
      toast.success(p.is_active ? "Paused." : "Resumed.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update"); }
  };

  const copyCode = async (p) => {
    if (!p.code) return;
    await navigator.clipboard.writeText(p.code);
    toast.success(`Copied ${p.code}`);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 pb-24 space-y-4" data-testid="vendor-promotions-page">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white" data-testid="promo-back">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      <header className="rounded-3xl border border-pink-400/30 bg-gradient-to-br from-pink-500/10 via-zinc-950 to-amber-500/10 p-5">
        <p className="inline-flex items-center gap-2 text-pink-300 text-[10px] uppercase tracking-[0.3em] font-black">
          <Ticket className="w-3 h-3" /> Vendor Marketing
        </p>
        <h1 className="text-3xl font-black tracking-tighter mt-1">Coupons, deals & offers</h1>
        <p className="text-sm text-zinc-400 mt-1">Give returning customers a reason to come back. Codes and auto-discounts apply at checkout automatically.</p>
        <div className="mt-3">
          <Button onClick={startCreate} className="rounded-full bg-pink-500 hover:bg-pink-400 text-white font-bold h-11 px-5" data-testid="promo-create-btn">
            <Plus className="w-4 h-4 mr-2" /> New promotion
          </Button>
        </div>
      </header>

      {loading ? (
        <div className="py-6 text-center text-zinc-500 text-sm inline-flex items-center gap-2 justify-center w-full"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-10 text-center text-zinc-500 text-sm" data-testid="promo-empty">
          No promotions yet. Create your first coupon or deal above.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((p) => {
            const T = TYPES.find((t) => t.value === p.type) || TYPES[0];
            return (
              <div key={p.promotion_id} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4" data-testid={`promo-row-${p.promotion_id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl grid place-items-center bg-pink-500/15 text-pink-300 shrink-0">
                    <T.Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-black tracking-tight">{p.name}</p>
                      <StatusPill status={p.status} />
                      {p.code && (
                        <button
                          onClick={() => copyCode(p)}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black bg-white/5 border border-white/10 text-zinc-200 hover:bg-white/10"
                          data-testid={`promo-code-${p.promotion_id}`}
                        >
                          <Copy className="w-3 h-3" /> {p.code}
                        </button>
                      )}
                      {p.auto_apply && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black bg-cyan-500/15 border border-cyan-400/40 text-cyan-100">Auto-applied</span>
                      )}
                      {p.code_public && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black bg-emerald-500/15 border border-emerald-400/40 text-emerald-100">Public</span>
                      )}
                    </div>
                    <p className="text-[11px] text-zinc-400 mt-0.5">
                      {p.type === "percent_off" && `${p.value}% off`}
                      {p.type === "flat_off" && `$${(p.value / 100).toFixed(2)} off`}
                      {p.type === "bogo" && `BOGO — buy ${p.value} get 1`}
                      {p.type === "free_shipping" && "Free shipping"}
                      {p.min_subtotal_cents ? ` · min $${(p.min_subtotal_cents / 100).toFixed(2)}` : ""}
                      {p.expires_at ? ` · expires ${new Date(p.expires_at).toLocaleDateString()}` : ""}
                    </p>
                    <p className="text-[11px] text-zinc-500 mt-0.5">
                      Redeemed {p.times_used || 0}{p.total_limit ? ` / ${p.total_limit}` : ""} · Per user limit {p.per_user_limit}
                    </p>
                    {p.description && <p className="text-xs text-zinc-300 mt-1">{p.description}</p>}
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button onClick={() => startEdit(p)} className="h-8 px-3 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-bold inline-flex items-center gap-1.5" data-testid={`promo-edit-${p.promotion_id}`}>
                    <Edit3 className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button onClick={() => togglePause(p)} className={`h-8 px-3 rounded-full border text-xs font-bold inline-flex items-center gap-1.5 ${p.is_active ? "bg-amber-500/15 border-amber-400/40 text-amber-100" : "bg-emerald-500/15 border-emerald-400/40 text-emerald-100"}`} data-testid={`promo-toggle-${p.promotion_id}`}>
                    {p.is_active ? <><Pause className="w-3.5 h-3.5" /> Pause</> : <><Play className="w-3.5 h-3.5" /> Resume</>}
                  </button>
                  <button onClick={() => remove(p)} className="h-8 px-3 rounded-full bg-rose-500/15 hover:bg-rose-500/25 border border-rose-400/40 text-rose-100 text-xs font-bold inline-flex items-center gap-1.5 ml-auto" data-testid={`promo-delete-${p.promotion_id}`}>
                    <Trash2 className="w-3.5 h-3.5" /> Archive
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 z-[95] grid place-items-center bg-black/85 backdrop-blur-sm p-4" onClick={() => setShowForm(false)} data-testid="promo-form-modal">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-zinc-950 rounded-3xl border border-pink-400/50" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-lg font-black tracking-tight">{editing ? "Edit promotion" : "New promotion"}</h3>
              <button onClick={() => setShowForm(false)} className="w-8 h-8 rounded-full grid place-items-center bg-white/5 hover:bg-white/10" data-testid="promo-form-close"><XCircle className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="space-y-1.5">
                <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Grand opening — 20% off" className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" data-testid="promo-form-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Type</Label>
                  <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="h-11 w-full bg-white/5 border border-white/10 rounded-2xl text-white px-3" data-testid="promo-form-type">
                    {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <p className="text-[10px] text-zinc-500">{(TYPES.find((t) => t.value === form.type) || {}).hint}</p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">
                    {form.type === "percent_off" ? "Percent (1–90)" : form.type === "flat_off" ? "Cents off" : form.type === "bogo" ? "Buy N (integer)" : "Value"}
                  </Label>
                  <Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" data-testid="promo-form-value" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Code (optional)</Label>
                  <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="OPENING20" maxLength={32} className="h-11 bg-white/5 border-white/10 rounded-2xl text-white uppercase tracking-widest" data-testid="promo-form-code" />
                  <label className="flex items-center gap-2 text-xs text-zinc-300 mt-1">
                    <input type="checkbox" checked={form.code_public} onChange={(e) => setForm({ ...form, code_public: e.target.checked })} className="accent-pink-500" data-testid="promo-form-code-public" />
                    Show code on public storefront
                  </label>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Or auto-apply</Label>
                  <label className={`flex items-center gap-2 h-11 px-4 rounded-2xl border cursor-pointer ${form.auto_apply ? "bg-cyan-500/10 border-cyan-400/40" : "bg-white/5 border-white/10"}`}>
                    <input type="checkbox" checked={form.auto_apply} onChange={(e) => setForm({ ...form, auto_apply: e.target.checked, code: e.target.checked ? "" : form.code })} className="accent-cyan-500" data-testid="promo-form-auto-apply" />
                    <span className="text-sm">Auto-apply when cart qualifies</span>
                  </label>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Min subtotal (cents)</Label>
                  <Input type="number" value={form.min_subtotal_cents} onChange={(e) => setForm({ ...form, min_subtotal_cents: e.target.value })} className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" data-testid="promo-form-min" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Per user limit</Label>
                  <Input type="number" min="1" value={form.per_user_limit} onChange={(e) => setForm({ ...form, per_user_limit: e.target.value })} className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" data-testid="promo-form-per-user" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Total limit (0 = ∞)</Label>
                  <Input type="number" min="0" value={form.total_limit} onChange={(e) => setForm({ ...form, total_limit: e.target.value })} className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" data-testid="promo-form-total" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Starts</Label>
                  <Input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" data-testid="promo-form-starts" />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Expires</Label>
                  <Input type="datetime-local" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" data-testid="promo-form-expires" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Description (optional)</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} maxLength={280} className="bg-white/5 border-white/10 rounded-2xl text-white" data-testid="promo-form-description" />
              </div>
            </div>
            <div className="p-4 border-t border-white/10 flex items-center gap-2 justify-end">
              <button onClick={() => setShowForm(false)} className="h-10 px-4 rounded-full bg-white/5 hover:bg-white/10 text-sm font-bold" data-testid="promo-form-cancel">Cancel</button>
              <Button onClick={save} disabled={saving} className="h-10 px-5 rounded-full bg-pink-500 hover:bg-pink-400 text-white text-sm font-bold" data-testid="promo-form-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
                {editing ? "Save changes" : "Create promotion"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
